// What the consent log's records say, in plain words.

// The most days one CSV export covers (content answers 400 beyond).
export const MAX_EXPORT_DAYS = 31

const ACTIONS = {
  allow_all: 'Allow all',
  essential_only: 'Essential only',
  save_choices: 'Saved choices',
  allow_embed: 'Allowed an embed'
}
const BANNERS = {
  opt_in: 'Opt-in banner',
  notice: 'Notice banner',
  gpc: 'Privacy signal banner'
}
const MODES = {
  'opt-in': 'Opt-in',
  notice: 'Notice'
}
// Why the visitor got opt-in or notice rules (the website's registry lists
// the opt-in countries and states or provinces; strict is the visitor's own
// ?consent-mode=strict). Empty for a record from before content kept it.
const MODE_REASONS = {
  listed_country: 'Listed country',
  listed_region: 'Listed state or province',
  not_listed: 'Not listed',
  unknown_location: 'Unknown location',
  strict: 'Visitor asked for strict'
}
const SITES = {
  website: 'Website',
  admin: 'Admin'
}

const words = (map, code) => (code ? map[code] || String(code) : '')
export const actionText = code => words(ACTIONS, code)
export const bannerText = code => words(BANNERS, code)
export const modeText = code => words(MODES, code)
export const modeReasonText = code => words(MODE_REASONS, code)
export const siteText = code => words(SITES, code)

// "US-CA": the country and, when the record has it, its state or province
// (the ISO 3166-2 subdivision); "US" without one, "" without a country.
export const locationText = (country, region) => (country ? (region ? `${country}-${region}` : country) : '')

// The first cell of the line content ends a failed export with
// (consentlogsys.IncompleteMarker): the file stopped partway after it began
// downloading (the answer was already 200), so it must not be relied on.
export const INCOMPLETE_MARKER = 'EXPORT INCOMPLETE'

// endsIncomplete: the CSV text's last non-empty line is content's
// EXPORT INCOMPLETE line (its first cell is exactly the marker; no record
// line starts with it, as a record starts with its time). cutAtStart says
// the text is the file's tail, whose first line may be cut short: it is
// never the one read.
export const endsIncomplete = (text, cutAtStart = false) => {
  const lines = String(text || '').split(/\r?\n/)
  if (cutAtStart) lines.shift()
  const last = lines.reverse().find(line => line.trim() !== '')
  if (!last) return false
  const comma = last.indexOf(',')
  const first = (comma < 0 ? last : last.slice(0, comma)).trim()
  return first === INCOMPLETE_MARKER || first === `"${INCOMPLETE_MARKER}"`
}

// "analytics on, marketing off" (categories in the order the record has them).
export const choicesText = choices =>
  Object.entries(choices || {}).map(([category, on]) => `${category} ${on ? 'on' : 'off'}`).join(', ')

// Whole days from..to counting both (YYYY-MM-DD), 0 for a missing or
// backwards range.
export const daysInRange = (from, to) => {
  if (!from || !to || to < from) return 0
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)
  return Number.isFinite(ms) ? Math.round(ms / 864e5) + 1 : 0
}

export const recordRow = r => ({
  id: String(r.seq),
  seq: r.seq,
  chosenAt: r.chosenAt || '',
  recordedAt: r.recordedAt || '',
  choices: r.choices || {},
  fingerprints: r.fingerprints || {},
  choicesText: choicesText(r.choices),
  actionText: actionText(r.action),
  bannerText: bannerText(r.banner),
  modeText: modeText(r.mode),
  modeReasonText: modeReasonText(r.modeReason),
  gpc: !!r.gpc,
  country: r.country || '',
  region: r.region || '',
  location: locationText(r.country, r.region),
  siteText: siteText(r.site),
  ipMasked: r.ipMasked || '',
  userAgent: r.userAgent || '',
  pagePath: r.pagePath || '',
  version: r.version || '',
  snapshot: r.snapshot || null
})

// The version snapshot's registry as categories, each with its providers and
// their cookies: [{ key, label, description, required, providers: [{ id,
// name, purpose, cookies: [{ name, lifetime, purpose, firstParty }] }] }].
// The registry lists providers flat, each naming its category; a category
// that nests its own providers is read the same.
export const registryCategories = registry => {
  if (!registry || typeof registry !== 'object') return []
  const categories = Array.isArray(registry.categories) ? registry.categories : []
  const providers = Array.isArray(registry.providers) ? registry.providers : []
  return categories.filter(c => c && typeof c === 'object').map(c => {
    const own = Array.isArray(c.providers) ? c.providers : []
    const listed = providers.filter(p => p && p.category === c.key)
    return {
      key: c.key,
      label: c.label || c.key,
      description: c.description || '',
      required: !!c.required,
      providers: [...own, ...listed].filter(p => p && typeof p === 'object').map(p => ({
        id: p.id || p.name,
        name: p.name || p.id,
        purpose: p.purpose || '',
        cookies: Array.isArray(p.cookies) ? p.cookies.filter(k => k && typeof k === 'object') : []
      }))
    }
  })
}

// The banner wording as key / value lines ("optIn.headline" -> text), in the
// stored order: content keeps the copy as jsonb, which orders an object's
// keys by length, then by name, so it is not the website file's order.
// "$comment" notes are the file's, not wording.
export const copyLines = copy => {
  const lines = []
  const walk = (value, path) => {
    if (value === null || value === undefined) return
    if (Array.isArray(value)) {
      if (value.every(v => v === null || typeof v !== 'object')) lines.push({ key: path, value: value.join(', ') })
      else value.forEach((v, i) => walk(v, `${path}.${i + 1}`))
      return
    }
    if (typeof value === 'object') {
      Object.entries(value).forEach(([k, v]) => { if (k !== '$comment') walk(v, path ? `${path}.${k}` : k) })
      return
    }
    lines.push({ key: path, value: String(value) })
  }
  walk(copy, '')
  return lines
}
